import React, { useState, useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import './label.css';
import { jsPDF } from 'jspdf';

const generateLabelsAsPDF = async (products) => {
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'cm',
        format: [10.5, 14.5] // Width x Height in centimeters
    });

    const totalLabels = products.reduce((sum, p) => sum + p.quantity, 0);
    const labelsPerPage = 15;
    const pages = Math.ceil(totalLabels / labelsPerPage);

    const pageWidth = 10.5;  // cm
    const pageHeight = 14.5; // cm
    const margin = 0.5;      // 0.5cm margins
    const labelWidth = 3.17; // (10.5 - 2*margin) / 3 columns
    const labelHeight = 2.7; // (14.5 - 2*margin) / 5 rows

    doc.setFontSize(7); // Smaller font size for labels

    let currentLabel = 0;
    
    for (const product of products) {
        for (let q = 0; q < product.quantity; q++) {
            const page = Math.floor(currentLabel / labelsPerPage);
            if (currentLabel > 0 && currentLabel % labelsPerPage === 0) {
                doc.addPage();
            }

            const positionInPage = currentLabel % labelsPerPage;
            const row = Math.floor(positionInPage / 3);
            const col = positionInPage % 3;

            const x = margin + (col * labelWidth);
            const y = margin + (row * labelHeight);

            // Reset font settings for each label's product title
            doc.setFontSize(5); // Set consistent small font size for product title
            doc.setTextColor(0); // Set text color to black

            const title = product.product_title;
            const lines = doc.splitTextToSize(title, labelWidth - 0.4);
            doc.text(lines, x + (labelWidth/2), y + 0.5, { align: 'center' });

            try {
                const canvas = document.createElement('canvas');
                JsBarcode(canvas, product.product_id.toString(), {
                    format: "CODE128",
                    width: 1,
                    height: 30,
                    displayValue: false
                });
                const barcodeImage = canvas.toDataURL('image/jpeg', 1.0);
                
                doc.addImage(
                    barcodeImage, 
                    'JPEG', 
                    x + 0.2,                    // More left margin
                    y + 1,                      
                    labelWidth - 0.4,           // More right margin
                    labelHeight - 1.5           
                );

                // Reset font settings for BYMO COLLECTIONS text
                doc.setFontSize(5);
                doc.setTextColor(150);
                doc.text("BYMO COLLECTIONS", x + (labelWidth/2), y + 2.3, { align: 'center' });
            } catch (error) {
                console.error('Error generating barcode:', error);
            }

            doc.setLineWidth(0.01);
            doc.setLineDashPattern([0.2, 0.2], 0);
            doc.setDrawColor(150);
            doc.rect(
                x, 
                y, 
                labelWidth, 
                labelHeight,
                'S'
            );

            currentLabel++;
        }
    }

    doc.save('labels.pdf');
};

const Label = ({ inventory }) => {
    const [labelSearchTerm, setLabelSearchTerm] = useState('');
    const [filteredLabelProducts, setFilteredLabelProducts] = useState([]);
    const [selectedLabelProducts, setSelectedLabelProducts] = useState([]);
    const [labelSaveStatus, setLabelSaveStatus] = useState('');

    useEffect(() => {
        const savedLabelProducts = sessionStorage.getItem('label_selected_products');
        if (savedLabelProducts) {
            setSelectedLabelProducts(JSON.parse(savedLabelProducts));
        }
    }, []);

    const handleLabelSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setLabelSearchTerm(term);
        
        if (term.length > 2) {
            const filtered = inventory?.filter(product => 
                product.product_title.toLowerCase().includes(term)
            ) || [];
            setFilteredLabelProducts(filtered);
        } else {
            setFilteredLabelProducts([]);
        }
    };

    const addProductToLabels = (product) => {
        if (!selectedLabelProducts.find(p => p.product_id === product.product_id)) {
            const newProducts = [...selectedLabelProducts, {
                product_id: product.product_id,
                product_title: product.product_title,
                quantity: 1 // Default quantity
            }];
            setSelectedLabelProducts(newProducts);
            sessionStorage.setItem('label_selected_products', JSON.stringify(newProducts));
            setLabelSaveStatus('Saved');
            setTimeout(() => setLabelSaveStatus(''), 2000);
        }
        setLabelSearchTerm('');
        setFilteredLabelProducts([]);
    };

    const updateLabelQuantity = (productId, newQuantity) => {
        const quantity = Math.max(1, parseInt(newQuantity) || 1);
        const newProducts = selectedLabelProducts.map(p => 
            p.product_id === productId ? { ...p, quantity } : p
        );
        setSelectedLabelProducts(newProducts);
        sessionStorage.setItem('label_selected_products', JSON.stringify(newProducts));
        setLabelSaveStatus('Updated');
        setTimeout(() => setLabelSaveStatus(''), 2000);
    };

    const removeProductFromLabels = (productId) => {
        const newProducts = selectedLabelProducts.filter(p => p.product_id !== productId);
        setSelectedLabelProducts(newProducts);
        sessionStorage.setItem('label_selected_products', JSON.stringify(newProducts));
        setLabelSaveStatus('Removed');
        setTimeout(() => setLabelSaveStatus(''), 2000);
    };

    return (
        <div className="label-container">
            <div className="label-controls">
                <div className="search-section">
                    <input
                        type="text"
                        value={labelSearchTerm}
                        onChange={handleLabelSearch}
                        placeholder="Search products for labels..."
                        className="search-input"
                    />
                    {filteredLabelProducts.length > 0 && (
                        <div className="search-results">
                            {filteredLabelProducts.map(product => (
                                <div 
                                    key={product.product_id} 
                                    className="search-result-item"
                                    onClick={() => addProductToLabels(product)}
                                >
                                    <span className="product-title">{product.product_title}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="selected-products">
                    <div className="selected-header">
                        <h3>Selected Products for Labels {labelSaveStatus && 
                            <span className="save-status">{labelSaveStatus}</span>}
                        </h3>
                        {selectedLabelProducts.length > 0 && (
                            <button 
                                onClick={() => generateLabelsAsPDF(selectedLabelProducts)}
                                className="generate-button"
                            >
                                Export Labels
                            </button>
                        )}
                    </div>
                    {selectedLabelProducts.length > 0 ? (
                        <div className="selected-list">
                            {selectedLabelProducts.map(product => (
                                <div key={product.product_id} className="selected-item">
                                    <span className="product-title">{product.product_title}</span>
                                    <div className="product-controls">
                                        <input
                                            type="number"
                                            min="1"
                                            value={product.quantity}
                                            onChange={(e) => updateLabelQuantity(product.product_id, e.target.value)}
                                            className="quantity-input"
                                        />
                                        <button 
                                            onClick={() => removeProductFromLabels(product.product_id)}
                                            className="remove-button"
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-products">No products selected for labels</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Label; 